import React, { Suspense, lazy } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import { PAYMENTS_ENABLED } from '../../constants/featureFlags';
import PublicLayout from '../layouts/PublicLayout';
import HomePage from '../../../pages/public/HomePage';
import { NavigationContext } from '../../../context/NavigationContext';
import PublicRoute from './PublicRoute';
import { useAuth } from '../../../context/AuthContext';
import FeedbackAnalysisPage from '../../../pages/private/feedback/FeedbackAnalysisPage';
import CustomTopicsPage from '../../../pages/private/feedback/CustomTopicsPage';
import FeedbackAskAI from '../../../pages/private/feedback/FeedbackAskAI';
import DynamicNotFound from '../../../pages/public/DynamicNotFound';

const TeamsPage = lazy(() => import('../../../pages/public/TeamsPage'));
const UseCasesPage = lazy(() => import('../../../pages/public/UseCasesPage'));
const AboutUsPage = lazy(() => import('../../../pages/public/AboutUsPage'));
const SupportPage = lazy(() => import('../../../pages/public/SupportPage'));
const PrivacySecurityPage = lazy(() => import('../../../pages/public/PrivacySecurityPage'));
const TermsOfService = lazy(() => import('../../../pages/public/components/terms-of-service/TermsOfService'));
const WebsiteTermsOfUse = lazy(() => import('../../../pages/public/components/website-terms-of-use/WebsiteTermsOfUse'));
const DPA = lazy(() => import('../../../pages/public/components/dpa/DPA'));
const PrivacyPolicy = lazy(() => import('../../../pages/public/components/privacy-policy/PrivacyPolicy'));
const SubscriptionPage = lazy(() => import('../../../pages/private/account/SubscribePage'));
const GetStartedPage = lazy(() => import('../../../pages/private/home/GetStartedPage'));
const NotFoundPage = lazy(() => import('../../../pages/public/NotFoundPage'));
const AppLayout = lazy(() => import('../layouts/AppLayout'));
const AppRoute = lazy(() => import('./AppRoute'));
const BlogPage = lazy(() => import('../../../pages/public/BlogPage'));
const BlogPostPage = lazy(() => import('../../../pages/public/BlogPostPage'));
const ScreenReplyPage = lazy(() => import('../../../pages/private/screen_record/ScreenReplyPage'));
const PricingPage = lazy(() => import('../../../pages/public/PricingPage'));
const AuthPage = lazy(() => import('../../../pages/public/AuthPage'));
const ValidateOnboardingPage = lazy(() => import('../../../pages/private/home/ValidateOnboardingPage'));
const OnboardingSetupPage = lazy(() => import('../../../pages/private/onboarding/OnboardingSetupPage'));
const SessionRecordScriptPage = lazy(() => import('../../../pages/private/screen_record/SessionRecordScriptPage'));
const UnifiedWebsiteAnalysisPage = lazy(() => import('../../../pages/public/private-reports/UnifiedWebsiteAnalysisPage'));
const WebsiteAnalysisPage = lazy(() => import('../../../pages/private/website-analysis/WebsiteAnalysisPage'));
const ReportsPage = lazy(() => import('../../../pages/private/website-analysis/ReportsPage'));
const ReportsList = lazy(() => import('../bars/side-bar/components/ReportsList'));
const CompetitorDetailPage = lazy(() => import('../../../pages/private/intelligence/CompetitorDetailPage'));
const ComparisonDetailPage = lazy(() => import('../../../pages/private/intelligence/ComparisonDetailPage'));
const AspectGridPage = lazy(() => import('../../../pages/private/intelligence/grid/AspectGridPage'));
const AiVisibilityPage = lazy(() => import('../../../pages/private/intelligence/AiVisibilityPage'));
const AspectDetailPage = lazy(() => import('../../../pages/private/intelligence/grid/AspectDetailPage'));
const CompanyScanPage = lazy(() => import('../../../pages/private/intelligence/CompanyScanPage'));
const SocialPage = lazy(() => import('../../../pages/private/intelligence/SocialPage'));
const ProgressPage = lazy(() => import('../../../pages/private/intelligence/grid/ProgressPage'));
const GscOauthCallbackPage = lazy(() => import('../../../pages/private/account/GscOauthCallbackPage'));
const BwtOauthCallbackPage = lazy(() => import('../../../pages/private/account/BwtOauthCallbackPage'));
const SeoKeywordDetailPage = lazy(() => import('../../../pages/private/seo/SeoKeywordDetailPage'));

let redirectPath = '/';
const RouteTable = () => {
  const { isLoading, isAuthenticated } = useAuth();
  const generateRoutesFromCategories = (categories, basePath = '/') => {
    let routes = [];

    categories.forEach((category) => {
      // Compute the full path
      let fullpath = category.path ? `${basePath}/${category.path}`.replace(/\/+/g, '/') : '';

      // Prepare component props if any
      const componentProps = category.params ? { ...category.params } : {};

      // If the category has a component, create a route for it
      if (category.component) {
        const route = (
          <AppRoute
            key={fullpath} // Ensure keys are unique
            exact={!!category.exact} // Use the exact flag from the category if provided
            path={fullpath}
            layout={(props) => (<AppLayout {...props} />)}
            component={(props) => React.createElement(category.component, { ...props, ...componentProps })}
          />
        );
        routes.push(route);
      }

      // If the category has children, recursively generate routes for them
      if (category.children) {
        const childRoutes = generateRoutesFromCategories(category.children, fullpath);
        routes = routes.concat(childRoutes); // Merge child routes into the current routes array
      }
    });

    return routes;
  };

  if (isLoading) {
    return <div></div>; // Or a loading spinner component
  }
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Switch>
        {/* App root: this used to render the legacy FullPivotPage
            ("Instant Market Intelligence") landing, a leftover from the
            historical product. Redirect to a sensible destination instead —
            the dashboard when signed in, otherwise the login page — so the
            stale page isn't reachable on the app subdomain and sign-out /
            bare-URL visits land somewhere clean. (Real marketing home is
            www.clientcues.com.) */}
        <Route
          exact
          path='/'
          render={() => (
            <Redirect to={localStorage.getItem('token')
              ? '/intelligence/ai-visibility'
              : '/login'} />
          )}
        />
        {/* Auth */}
        <Route
          path={[
            '/login',
            '/signup',
            '/register',
            '/reset_password',
            '/verify_email',
            '/link_validate_email',
            '/forgot_password',
            '/link_validate_email_set_password',
          ]}
          exact
          component={AuthPage}
        />
        {/* Teams */}
        <PublicRoute
          exact
          path='/teams/*'
          layout={PublicLayout}
          component={TeamsPage}
          redirectPath={redirectPath}
          title={'Market Intelligence for Teams: Align with Strategic Product Planning'}
          description={
            'Empower your product, marketing, and support teams with actionable market intelligence, competitor analysis, and customer feedback tools for unified growth and smarter decision-making.'
          }
        />
        {/* Use Cases */}
        <PublicRoute
          exact
          path='/use-cases/*'
          layout={PublicLayout}
          component={UseCasesPage}
          redirectPath={redirectPath}
          title={'Use Cases: Competitor Analysis, Market Research & Growth Trend Recognition'}
          description={
            'Explore real-world use cases for market trend analysis, competitor intelligence, and actionable customer feedback. Drive conversion and strategic product innovation with ClientCues.'
          }
        />
        {/* Pricing */}
        <PublicRoute
          exact
          path='/pricing'
          layout={PublicLayout}
          component={PricingPage}
          redirectPath={redirectPath}
          title={'ClientCues Pricing: Market Intelligence & Competitor Analysis Plans'}
          description={
            'See transparent pricing for instant competitor comparisons, deep market trend analysis, user feedback, and actionable market research. Choose your growth-driven plan today.'
          }
        />
        {/* Blog */}
        <PublicRoute
          exact
          path='/company/blog'
          layout={PublicLayout}
          component={BlogPage}
          redirectPath={redirectPath}
          title={'ClientCues Blog: Actionable Market Research & Competitive Intelligence'}
          description={
            'Read expert articles on competitor analysis, customer feedback UI design, market intelligence, strategic product planning, growth trends, and more to stay ahead of your market.'
          }
        />
        {/* Blog Posts */}
        <PublicRoute
          exact
          path='/company/blog/posts/*'
          layout={PublicLayout}
          component={BlogPostPage}
          redirectPath={redirectPath}
        />
        {/* About Us */}
        <PublicRoute
          exact
          path='/company/about-us'
          layout={PublicLayout}
          component={AboutUsPage}
          redirectPath={redirectPath}
          title={'About ClientCues: Leading Market Intelligence & Product Strategy'}
          description={
            'Discover our vision for AI-driven market intelligence, strategic product planning, competitor analysis, and customer-centric innovation. Meet the team powering next-gen business growth.'
          }
        />
        {/* Support */}
        <PublicRoute
          exact
          path='/company/support'
          layout={PublicLayout}
          component={SupportPage}
          redirectPath={redirectPath}
          title={'ClientCues Support: Market Intelligence & Platform Guidance'}
          description={
            'Get help with competitor analysis, market intelligence setup, AI integrations, and feedback tools. Our experts support your data-driven growth and strategic execution.'
          }
        />
        {/* Security */}
        <PublicRoute
          exact
          path='/company/security'
          layout={PublicLayout}
          component={PrivacySecurityPage}
          redirectPath={redirectPath}
          title={'Enterprise Security for Market Intelligence & User Data'}
          description={
            'Learn how ClientCues safeguards your competitor and customer data. Explore our secure platform architecture for market research, session replay, and feedback analytics.'
          }
        />
        {/* Terms of Service */}
        <PublicRoute
          exact
          path='/legal/terms-of-service'
          layout={PublicLayout}
          component={TermsOfService}
          redirectPath={redirectPath}
          title={'ClientCues Terms of Service: Market Intelligence Platform'}
          description={
            'Review our terms for accessing market intelligence, competitor analysis, session recording, and customer feedback solutions.'
          }
        />
        {/* Website Terms of Use */}
        <PublicRoute
          exact
          path='/legal/website-terms-of-use'
          layout={PublicLayout}
          component={WebsiteTermsOfUse}
          redirectPath={redirectPath}
          title={'ClientCues Website Terms of Use: Market & Feedback Analytics'}
          description={
            'See the terms for using the ClientCues website, market research tools, competitor comparisons, and user feedback services.'
          }
        />
        {/* DPA */}
        <PublicRoute
          exact
          path='/legal/dpa'
          layout={PublicLayout}
          component={DPA}
          redirectPath={redirectPath}
          title={'ClientCues Data Processing Agreement: Secure Market Intelligence'}
          description={
            'Understand our commitment to secure processing of customer, market, and competitor analytics data within ClientCues.'
          }
        />
        {/* Privacy Policy */}
        <PublicRoute
          exact
          path='/legal/privacy-policy'
          layout={PublicLayout}
          component={PrivacyPolicy}
          redirectPath={redirectPath}
          title={'ClientCues Privacy Policy: Protecting Market & User Data'}
          description={
            'Learn how your data is collected, analyzed, and protected through our market intelligence, competitor analysis, and customer feedback solutions.'
          }
        />
        {/* Public session replay */}
        <PublicRoute
          exact
          path='/public/session-replay/replay_session'
          layout={PublicLayout}
          component={ScreenReplyPage}
          title="Session Replay: Analyze User Experience Instantly"
          description="Secure, shareable session replay for direct market intelligence on user journeys, friction points, and competitor benchmarks."
        />
        {/* Demo Website Analysis */}
        <PublicRoute
          exact
          path='/public/analysis_report'
          layout={PublicLayout}
          component={UnifiedWebsiteAnalysisPage}
          title="Market Analysis Report: Actionable Insights for Any Website"
          description="Instantly generate a market research and competitor analysis report—compare features, pricing, and user feedback to drive better decisions."
        />
        {/* Onboarding Setup - minimal layout (no sidebar) */}
        <AppRoute
          exact
          path='/onboarding/setup'
          layout={({ children }) => <div>{children}</div>}
          component={OnboardingSetupPage}
        />
        {/* GSC OAuth callback — runs inside a popup window opened by */}
        {/* the Integrations page. No layout chrome; closes itself.   */}
        <AppRoute
          exact
          path='/auth/gsc/callback'
          layout={({ children }) => <div>{children}</div>}
          component={GscOauthCallbackPage}
        />
        {/* Bing Webmaster OAuth callback — same popup pattern as GSC. */}
        <AppRoute
          exact
          path='/auth/bing/callback'
          layout={({ children }) => <div>{children}</div>}
          component={BwtOauthCallbackPage}
        />
        {/* Account, onboarding, and feedback AppRoutes */}
        <AppRoute
          key='/settings/edit-plan'
          exact={true}
          path='/settings/edit-plan'
          layout={props => <AppLayout {...props} />}
          component={props => <PricingPage isPublic={false} />}
        />
        {PAYMENTS_ENABLED ? (
          <AppRoute
            key='/settings/subscribe'
            exact={true}
            path='/settings/subscribe'
            layout={props => <AppLayout {...props} />}
            component={props => <SubscriptionPage />}
          />
        ) : (
          <Route
            key='/settings/subscribe'
            exact
            path='/settings/subscribe'
            render={() => <Redirect to='/' />}
          />
        )}
        <AppRoute
          exact
          path='/home/validate-onboarding'
          layout={props => <AppLayout {...props} />}
          component={ValidateOnboardingPage}
        />
        <AppRoute
          exact
          path='/home/install-script'
          layout={props => <AppLayout {...props} />}
          component={SessionRecordScriptPage}
        />
        <AppRoute
          key='/home/get-started'
          exact={true}
          path='/home/get-started'
          layout={props => <AppLayout {...props} />}
          component={props => <GetStartedPage />}
        />
        <AppRoute
          key='/feedback/analysis'
          exact={true}
          path='/feedback/analysis'
          layout={props => <AppLayout {...props} />}
          component={props => <FeedbackAnalysisPage />}
        />
        <AppRoute
          key='/feedback/ask_ai'
          exact={true}
          path='/feedback/ask_ai'
          layout={props => <AppLayout {...props} />}
          component={props => <FeedbackAskAI />}
        />
        <AppRoute
          key='/feedback/validate'
          exact={true}
          path='/feedback/validate'
          layout={props => <AppLayout {...props} />}
          component={props => <CustomTopicsPage />}
        />
        <AppRoute
          key='/session_replay/replay_session'
          exact={true}
          path='/session_replay/replay_session'
          layout={props => <AppLayout {...props} />}
          component={props => <ScreenReplyPage />}
        />
        <AppRoute
          key='/market_trend_reporting/analyze'
          exact={true}
                      path='/market_trend_reporting/analyze'
          layout={props => <AppLayout {...props} />}
          component={props => <WebsiteAnalysisPage />}
        />
        <AppRoute
          key='/market_trend_reporting/reports'
          exact={true}
                      path='/market_trend_reporting/reports'
          layout={props => <AppLayout {...props} />}
          component={props => <ReportsPage />}
        />
        <AppRoute
          key='/market_trend_reporting/report'
          exact={true}
                      path='/market_trend_reporting/report'
          layout={props => <AppLayout {...props} />}
          component={props => <UnifiedWebsiteAnalysisPage />}
        />
        <AppRoute
          key='/market_trend_reporting/recent_reports'
          exact={true}
                      path='/market_trend_reporting/recent_reports'
          layout={props => <AppLayout {...props} />}
          component={props => <ReportsList />}
        />
        {/* AI Visibility — the value-first landing: do ChatGPT/Gemini/Claude
            recommend you or a competitor, share of voice, and a one-click
            site GEO-readiness audit. Default home after login. */}
        <AppRoute
          key='/intelligence/ai-visibility'
          exact={true}
          path='/intelligence/ai-visibility'
          layout={props => <AppLayout {...props} />}
          component={props => <AiVisibilityPage {...props} />}
        />
        {/* Aspect Grid — the streamlined intelligence landing. Rows are
            business aspects, columns are competitors; cells show your
            standing vs each. Replaces the old dashboard as the home. */}
        <AppRoute
          key='/intelligence/grid'
          exact={true}
          path='/intelligence/grid'
          layout={props => <AppLayout {...props} />}
          component={props => <AspectGridPage {...props} />}
        />
        {/* Per-aspect detail — tabs: Action plan / Improve coverage / Progress.
            Opened from a row's action button / coverage button / progress list. */}
        <AppRoute
          key='/intelligence/aspect/:aspectId'
          exact={true}
          path='/intelligence/aspect/:aspectId'
          layout={props => <AppLayout {...props} />}
          component={props => <AspectDetailPage {...props} />}
        />
        {/* Progress — daily-logged score list across aspects. */}
        <AppRoute
          key='/intelligence/progress'
          exact={true}
          path='/intelligence/progress'
          layout={props => <AppLayout {...props} />}
          component={props => <ProgressPage {...props} />}
        />
        {/* Reddit post-generation workflow (replaces the suppressed Social aspect). */}
        <AppRoute
          key='/intelligence/social'
          exact={true}
          path='/intelligence/social'
          layout={props => <AppLayout {...props} />}
          component={props => <SocialPage {...props} />}
        />
        {/* Full raw scan for one company — every field the flow returned (opened from
            a company's "View full scan" link in the action-page raw-sources view). */}
        <AppRoute
          key='/intelligence/company-scan/:companyId'
          exact={true}
          path='/intelligence/company-scan/:companyId'
          layout={props => <AppLayout {...props} />}
          component={props => <CompanyScanPage {...props} />}
        />
        {/* Competitor Intelligence - detail page (dynamic route) */}
        <AppRoute
          key='/intelligence/competitor/:companyId'
          exact={true}
          path='/intelligence/competitor/:companyId'
          layout={props => <AppLayout {...props} />}
          component={props => <CompetitorDetailPage {...props} />}
        />
        {/* Competitor Comparisons - detail page (dynamic route) */}
        <AppRoute
          key='/intelligence/comparison/:comparisonId'
          exact={true}
          path='/intelligence/comparison/:comparisonId'
          layout={props => <AppLayout {...props} />}
          component={props => <ComparisonDetailPage {...props} />}
        />
        {/* SEO Keyword - detail page (dynamic route) */}
        {/* Must be registered BEFORE the auto-generated /seo/keywords */}
        {/* route so React Router v5 prefers the more specific :id match. */}
        <AppRoute
          key='/seo/keywords/:keywordId'
          exact={true}
          path='/seo/keywords/:keywordId'
          layout={props => <AppLayout {...props} />}
          component={props => <SeoKeywordDetailPage {...props} />}
        />
        {/* Auth navigation */}
        {isAuthenticated &&
          <NavigationContext.Consumer>
            {({ categoryList }) => categoryList ?
              generateRoutesFromCategories(categoryList)
              : null //or some loading spinner
            }
          </NavigationContext.Consumer>
        }
        {/* Not Found */}
        <Route
          render={(props) => {
            // Our custom catch-all:
            return <DynamicNotFound {...props} />;
          }}
        />
      </Switch>
    </Suspense>
  );
};

export default RouteTable;